(() => {
  if (customElements.get('fgg-product-section-nav')) return;
  class FggProductSectionNav extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      const other = document.querySelector('fgg-product-section-nav[data-initialized]');
      if (other && other !== this) return;
      this.abort = new AbortController();
      this.dataset.initialized = 'true';
      this.restores = [];
      this.pageIndicator = this.dataset.designMode === 'true' ? document.getElementById('FggNavPageIndicator-' + this.dataset.sectionId) : null;
      this.list = this.querySelector('.fgg-section-nav__links');
      this.toggle = this.querySelector('.fgg-section-nav__toggle');
      this.mobile = matchMedia('(max-width: 999px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      try {
        this.config = JSON.parse(this.querySelector('[data-fgg-nav-config]').textContent);
        this.map = JSON.parse(this.querySelector('[data-fgg-nav-map]')?.textContent || '[]');
        const entries = this.querySelector('[data-fgg-nav-entries]')?.content;
        this.candidates = this.dataset.useDefaults === 'true'
          ? this.config.targets.split('|').filter(Boolean).map(text => {
            const [key, target] = text.split('@');
            return { label: this.config.labels[key], target, review: key === 'reviews' };
          })
          : [...(entries?.children || [])].filter(x => x.dataset.enabled !== 'false').map(node => ({
            node, blockId: node.dataset.blockId, label: node.dataset.label,
            mode: node.dataset.mode, destination: node.dataset.destination,
            instance: Number(node.dataset.instance || 1), frameNumber: Number(node.dataset.frame || 1),
            pageNumber: Number(node.dataset.pageNumber || 1), frameTitle: node.dataset.frameTitle, anchor: node.dataset.anchor, target: node.dataset.target
          }));
      } catch { return; }
      const listen = (node, name, callback, options = {}) => node.addEventListener(name, callback, { ...options, signal: this.abort.signal });
      listen(this.toggle, 'click', () => this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true'));
      listen(this, 'keydown', event => {
        if (event.key === 'Escape') { this.setExpanded(false); this.toggle.focus(); }
      });
      listen(this.list, 'click', event => {
        const link = event.target.closest('a[data-fgg-nav-link]');
        if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const item = this.items.find(x => x.link === link);
        if (!item?.target.isConnected) return;
        event.preventDefault();
        this.jump(item);
        if (this.mobile.matches) this.setExpanded(false);
      });
      listen(window, 'resize', () => this.schedule(true), { passive: true });
      listen(window, 'scroll', () => this.queueTextCollision(), { passive: true });
      listen(window, 'pageshow', () => this.schedule(true));
      listen(this.mobile, 'change', () => this.schedule(true));
      listen(document, 'shopify:section:load', () => this.schedule(true, true));
      listen(document, 'shopify:section:unload', () => this.schedule(true, true));
      listen(document, 'shopify:section:reorder', () => this.schedule(true, true));
      listen(document, 'shopify:block:select', event => {
        const item = this.items.find(x => x.blockId === event.detail?.blockId);
        if (item) { this.setExpanded(true); this.jump(item, 'auto'); }
      });
      this.header = document.getElementById('shopify-section-header');
      this.sticky = [...document.querySelectorAll('product-sticky-form, .product-sticky-form')];
      this.overlays = [...document.querySelectorAll('drawer-content, cart-drawer, #mini-cart, dialog')];
      this.overlayObserver = new MutationObserver(() => this.schedule());
      this.overlays.forEach(node => this.overlayObserver.observe(node, { attributes: true, attributeFilter: ['open', 'aria-hidden', 'class'] }));
      this.build();
      this.setExpanded(this.dataset.defaultExpanded !== 'false');
      this.schedule(true);
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(true); });
    }
    resolve(candidate, main, sections) {
      if (candidate.mode === 'page_number') {
        return Number.isSafeInteger(candidate.pageNumber) && candidate.pageNumber > 0 ? this.pages[candidate.pageNumber - 1] || null : null;
      }
      const name = candidate.target || candidate.destination;
      if (candidate.mode === 'anchor') {
        if (!candidate.anchor) return null;
        const matches = [...main.querySelectorAll('[data-fgg-nav-anchor]')].filter(x => x.dataset.fggNavAnchor === candidate.anchor);
        return matches.length === 1 ? { target: matches[0] } : null;
      }
      if (name === 'confidence') {
        const matches = [...main.querySelectorAll('.fgg-product-confidence')];
        return matches.length === 1 ? { target: matches[0] } : null;
      }
      if (name === 'support') {
        const content = sections.find(x => x.id.endsWith('__product-content'));
        const scoped = [...(content?.querySelectorAll('.fgg-support-resources') || [])];
        const matches = scoped.length ? scoped : [...main.querySelectorAll('.fgg-support-resources')];
        return matches.length === 1 ? { target: matches[0] } : null;
      }
      let matches;
      if (candidate.target) matches = sections.filter(x => x.id.endsWith('__' + candidate.target));
      else {
        const type = candidate.mode === 'story_frame' ? 'fgg-product-scroll-story' : candidate.destination;
        const keys = this.map.filter(x => x.type === type && !x.disabled);
        const key = keys[Math.max(0, candidate.instance - 1)]?.key;
        matches = key ? sections.filter(x => x.id.endsWith('__' + key)) : [];
      }
      if (matches.length !== 1) return null;
      const section = matches[0];
      if (candidate.mode !== 'story_frame') {
        if (candidate.review && !section.querySelector('.jdgm-review-widget, .jdgm-rev-widg')) return null;
        if (candidate.destination === 'apps' && !section.querySelector('.jdgm-review-widget, .jdgm-rev-widg')) return null;
        return { target: section };
      }
      const story = section.querySelector('[data-scroll-story]');
      const frames = [...(story?.querySelectorAll('[data-story-frame]') || [])];
      const normalize = text => text?.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
      let frame;
      if (candidate.frameTitle?.trim()) {
        const exact = frames.filter(x => normalize(x.querySelector('.story-title')?.textContent) === normalize(candidate.frameTitle));
        if (exact.length !== 1) return null;
        frame = exact[0];
      } else frame = frames[candidate.frameNumber - 1];
      if (!frame) return null;
      const index = frames.indexOf(frame);
      return { story, frame, frames, index, section, fraction: frames.length > 1 ? index / (frames.length - 1) : 0, key: section.id + '-frame-' + index };
    }
    storyTarget(resolved) {
      const id = 'FggNavFrame-' + this.dataset.product + '-' + resolved.section.id.split('__').pop() + '-' + resolved.index;
      const existing = document.getElementById(id);
      if (existing) return existing.classList.contains('fgg-nav-story-anchor') && existing.parentElement === resolved.story ? existing : null;
      const target = document.createElement('span'); target.id = id; target.className = 'fgg-nav-story-anchor';
      resolved.story.append(target); this.restores.push(() => target.remove());
      return target;
    }
    pageRegistry(sections) {
      const pages = [];
      for (const section of sections) {
        if (section.parentElement?.closest('.shopify-section') || section.matches('.fgg-section-nav-wrapper, .fgg-diy-nav-anchor-wrapper') || section.querySelector('fgg-product-section-nav, [data-fgg-nav-anchor]')) continue;
        if (section.closest('[hidden], [aria-hidden="true"]') || !section.getClientRects().length || section.getBoundingClientRect().height <= 1 || getComputedStyle(section).display === 'none') continue;
        const mapped = this.map.find(m => section.id.endsWith('__' + m.key));
        // Ignore script-only/empty app wrappers; actual content, not template slots, is numbered.
        const copy = section.cloneNode(true); copy.querySelectorAll('script, style, template, noscript').forEach(x => x.remove());
        if (!copy.textContent.trim() && !copy.querySelector('img, video, iframe, canvas, svg, model-viewer')) continue;
        const story = section.querySelector('[data-scroll-story]');
        const frames = [...(story?.querySelectorAll('[data-story-frame]') || [])];
        const heading = section.querySelector('h1, h2, h3')?.textContent.trim() || mapped?.type || '内容区块';
        if (frames.length) {
          frames.forEach((frame, index) => {
            const page = { story, frame, frames, index, section, fraction: frames.length > 1 ? index / (frames.length - 1) : 0, key: section.id + '-frame-' + index, pageLabel: frame.querySelector('.story-title')?.textContent.trim() || heading + ' · 图片 ' + (index + 1) };
            page.target = this.storyTarget(page); if (page.target) pages.push(page);
          });
        } else pages.push({ target: section, section, pageLabel: heading });
      }
      return pages;
    }
    currentItem(items) {
      if (!items?.length) return null;
      let active = items[0], top = -Infinity;
      for (const item of items) {
        const y = item.target.getBoundingClientRect().top;
        if (y <= this.headerOffset + 100 && y > top) { top = y; active = item; }
      }
      return items.find(item => item.story && item.frame.classList.contains('is-active') && item.story.getBoundingClientRect().top <= this.headerOffset && item.story.getBoundingClientRect().bottom > this.headerOffset + 100) || active;
    }
    build() {
      this.resizeObserver?.disconnect(); this.storyObserver?.disconnect(); this.reviewObservers?.forEach(x => x.disconnect());
      this.restores.reverse().forEach(restore => restore()); this.restores = [];
      const main = document.getElementById('main');
      if (!main) return;
      const sections = [...main.querySelectorAll('.shopify-section[id]')];
      this.pages = this.pageIndicator || this.candidates.some(x => x.mode === 'page_number') ? this.pageRegistry(sections) : [];
      const used = new Set(); this.items = []; this.list.replaceChildren();
      for (const candidate of this.candidates) {
        if (!candidate.label?.trim()) continue;
        const resolved = this.resolve(candidate, main, sections);
        if (!resolved) continue;
        const owner = resolved.section || resolved.target;
        if (!owner.isConnected || owner.closest('[hidden], [aria-hidden="true"]') || !owner.getClientRects().length) continue;
        const unique = resolved.key || resolved.target;
        if (used.has(unique)) continue;
        let target = resolved.target;
        if (resolved.story) {
          target = this.storyTarget(resolved); if (!target) continue;
        } else if (!target.id) {
          const id = 'FggNavTarget-' + this.dataset.product + '-' + (candidate.target || candidate.destination);
          if (document.getElementById(id)) continue;
          target.id = id; this.restores.push(() => { if (target.id === id) target.removeAttribute('id'); });
        }
        used.add(unique);
        const margin = target.style.scrollMarginTop;
        this.restores.push(() => { target.style.scrollMarginTop = margin; });
        const label = candidate.target === 'confidence' && !target.querySelector('.fgg-product-confidence__links') ? this.config.labels.warranty : candidate.label;
        const item = { ...resolved, target, label, blockId: candidate.blockId };
        const li = candidate.node?.cloneNode(true) || document.createElement('li');
        const link = document.createElement('a'); link.href = '#' + target.id; link.dataset.fggNavLink = ''; link.setAttribute('aria-label', label);
        const dot = document.createElement('span'); dot.className = 'fgg-section-nav__dot'; dot.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span'); text.className = 'fgg-section-nav__label'; text.textContent = label;
        link.append(dot, text); li.append(link); this.list.append(li); item.link = link; this.items.push(item);
      }
      this.hidden = this.items.length === 0;
      this.resizeObserver = new ResizeObserver(() => this.schedule(true));
      [this.header, ...this.sticky, ...new Set([...this.items.map(x => x.story), ...this.pages.map(x => x.section)].filter(Boolean))].filter(Boolean).forEach(x => this.resizeObserver.observe(x));
      this.storyObserver = new MutationObserver(() => {
        const state = [...this.items, ...this.pages].filter(x => x.frame?.classList.contains('is-active')).map(x => x.key).join('|');
        if (state !== this.storyState) { this.storyState = state; this.schedule(); }
      });
      [...this.items, ...this.pages].filter(x => x.frame).forEach(x => this.storyObserver.observe(x.frame, { attributes: true, attributeFilter: ['class'] }));
      this.reviewObservers = [];
      for (const section of sections.filter(x => this.map.some(m => m.type === 'apps' && x.id.endsWith('__' + m.key)))) {
        if (section.querySelector('.jdgm-review-widget, .jdgm-rev-widg')) continue;
        const observer = new MutationObserver(() => {
          if (section.querySelector('.jdgm-review-widget, .jdgm-rev-widg')) { observer.disconnect(); this.schedule(true, true); }
        });
        observer.observe(section, { childList: true, subtree: true }); this.reviewObservers.push(observer);
      }
      this.watchTextCollision();
    }
    watchTextCollision() {
      this.textViewportObserver?.disconnect(); this.textContentObserver?.disconnect(); this.videoViewportObserver?.disconnect();
      const main = document.getElementById('main');
      this.textGroups = new Map(); this.visibleText = new Set(); this.textRegistryDirty = false;
      this.videoRegions = []; this.detailSections = [];
      if (!main) return;
      this.detailSections = [...main.querySelectorAll('.shopify-section[id]')].filter(section => !section.parentElement?.closest('.shopify-section') && !section.matches('.fgg-section-nav-wrapper, .fgg-diy-nav-anchor-wrapper') && !section.querySelector('fgg-product-section-nav, [data-fgg-nav-anchor]'));
      this.videoRegions = [...main.querySelectorAll('video, iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"], iframe[src*="player.vimeo.com/video/"]')];
      this.videoViewportObserver = new IntersectionObserver(() => this.queueTextCollision(), { threshold: 0 });
      this.videoRegions.forEach(video => this.videoViewportObserver.observe(video));
      const ignored = 'fgg-product-section-nav, .fgg-nav-page-indicator, script, style, template, noscript, svg, textarea, select, [hidden], [aria-hidden="true"]';
      const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const parent = node.parentElement;
        if (!node.textContent.trim() || !parent || parent.closest(ignored)) continue;
        if (!this.textGroups.has(parent)) this.textGroups.set(parent, []);
        this.textGroups.get(parent).push(node);
      }
      this.visibleText = new Set(this.textGroups.keys());
      this.textViewportObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.visibleText.add(entry.target);
          else this.visibleText.delete(entry.target);
        }
        this.queueTextCollision();
      }, { threshold: 0 });
      this.textGroups.forEach((nodes, parent) => this.textViewportObserver.observe(parent));
      this.textContentObserver = new MutationObserver(records => {
        if (!records.some(record => {
          const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return target && !target.closest(ignored);
        })) return;
        this.textRegistryDirty = true; this.queueTextCollision();
      });
      this.textContentObserver.observe(main, { childList: true, characterData: true, subtree: true });
    }
    queueTextCollision() {
      if (this.textCollisionTimer || !this.abort) return;
      this.textCollisionTimer = setTimeout(() => {
        this.textCollisionTimer = null;
        if (!this.abort) return;
        if (this.textRegistryDirty) this.watchTextCollision();
        this.schedule();
      }, 120);
    }
    overlapsPageText(rect) {
      const intersects = box => box.width > 0 && box.height > 0 && box.left < rect.right && box.right > rect.left && box.top < rect.bottom && box.bottom > rect.top;
      const range = document.createRange();
      for (const parent of this.visibleText || []) {
        if (!parent.isConnected || !intersects(parent.getBoundingClientRect())) continue;
        if (parent.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) === false) continue;
        const style = getComputedStyle(parent);
        if (style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) continue;
        for (const node of this.textGroups.get(parent) || []) {
          if (!node.isConnected || !node.textContent.trim()) continue;
          range.selectNodeContents(node);
          if ([...range.getClientRects()].some(intersects)) return true;
        }
      }
      return false;
    }
    overlapsPageVideo(rect) {
      return (this.videoRegions || []).some(video => {
        if (!video.isConnected || video.closest('[hidden], [aria-hidden="true"]')) return false;
        if (video.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) === false) return false;
        const box = video.getBoundingClientRect(), style = getComputedStyle(video);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && Number(style.opacity) > 0 && box.width > 0 && box.height > 0 && box.left < rect.right && box.right > rect.left && box.top < rect.bottom && box.bottom > rect.top;
      });
    }
    beforeDetailStart() {
      const intro = (this.videoRegions || []).find(video => {
        if (!video.isConnected || !video.closest('.fgg-device-video') || video.closest('[hidden], [aria-hidden="true"]')) return false;
        if (video.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) === false) return false;
        const box = video.getBoundingClientRect(), style = getComputedStyle(video);
        return box.width > 0 && box.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0;
      })?.closest('.shopify-section');
      const index = (this.detailSections || []).indexOf(intro);
      if (!intro || index < 0) {
        const sections = this.detailSections || [];
        const topEnd = sections.reduce((last, section, position) => {
          const type = (this.map || []).find(entry => section.id.endsWith('__' + entry.key))?.type;
          return section.id.endsWith('__main') || ['main-product', 'folded-product-parameters'].includes(type) ? position : last;
        }, -1);
        const firstImage = sections.slice(topEnd + 1).find(section => {
          if (!section.isConnected || section.closest('[hidden], [aria-hidden="true"]')) return false;
          const box = section.getBoundingClientRect(), style = getComputedStyle(section);
          if (box.width <= 0 || box.height <= 1 || style.display === 'none' || style.visibility === 'hidden') return false;
          return [...section.querySelectorAll('img')].some(image => {
            if (image.closest('[hidden], [aria-hidden="true"]') || image.checkVisibility?.({ checkVisibilityCSS: true }) === false) return false;
            const imageBox = image.getBoundingClientRect();
            return imageBox.width > 0 && imageBox.height > 1;
          });
        });
        return firstImage ? firstImage.getBoundingClientRect().top > this.headerOffset + 1 : false;
      }
      const next = this.detailSections.slice(index + 1).find(section => {
        if (!section.isConnected || section.closest('[hidden], [aria-hidden="true"]')) return false;
        const box = section.getBoundingClientRect(), style = getComputedStyle(section);
        return box.width > 0 && box.height > 1 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0;
      });
      return (next ? next.getBoundingClientRect().top : intro.getBoundingClientRect().bottom) > this.headerOffset + 1;
    }
    setExpanded(expanded) {
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.classList.toggle('is-expanded', expanded);
      this.schedule(true);
    }
    setOffsets() {
      const height = this.header?.getBoundingClientRect().height || parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
      this.headerOffset = Math.ceil(height + 16);
      for (const item of new Set([...(this.items || []), ...(this.pages || [])])) {
        if (item.story) item.target.style.top = Math.max(0, item.story.offsetHeight - innerHeight) * item.fraction + 'px';
        item.target.style.scrollMarginTop = item.story ? '0px' : this.headerOffset + 'px';
      }
      let bottom = 24;
      for (const sticky of this.sticky || []) {
        const rect = sticky.getBoundingClientRect(),style = getComputedStyle(sticky);
        if (style.visibility !== 'hidden' && Number(style.opacity) > 0 && rect.bottom > innerHeight - 4 && rect.top < innerHeight && ['fixed', 'sticky'].includes(style.position)) bottom = Math.max(bottom, innerHeight - Math.max(0, rect.top) + 12);
      }
      this.pageIndicator?.style.setProperty('--fgg-page-bottom', Math.max(12, bottom - 12) + 'px');
      this.style.setProperty('--fgg-nav-bottom', bottom + 'px');
      this.style.setProperty('--fgg-nav-available', Math.max(100, innerHeight - this.headerOffset - bottom - 64) + 'px');
      const naturalHeight = this.offsetHeight;
      const availableHeight = Math.max(1, innerHeight - this.headerOffset - bottom - 24);
      const fit = naturalHeight > 0 ? Math.min(1, availableHeight / naturalHeight) : 1;
      this.style.setProperty('--fgg-nav-fit', String(fit));
      const halfHeight = naturalHeight * fit / 2;
      const center = Math.max(this.headerOffset + 12 + halfHeight, Math.min(innerHeight / 2, innerHeight - bottom - 12 - halfHeight));
      this.style.setProperty('--fgg-nav-top', center + 'px');
    }
    jump(item, behavior) {
      this.setOffsets();
      const focus = item.frame || item.target;
      if (!focus.hasAttribute('tabindex')) {
        focus.setAttribute('tabindex', '-1');
        const restore = () => focus.removeAttribute('tabindex');
        focus.addEventListener('blur', restore, { once: true, signal: this.abort.signal }); this.restores.push(restore);
      }
      focus.focus({ preventScroll: true });
      item.target.scrollIntoView({ block: 'start', behavior: behavior || (this.reduced.matches ? 'auto' : 'smooth') });
      this.activate(item);
    }
    observe() {
      this.observer?.disconnect(); this.collisionObserver?.disconnect();
      if (!this.items?.length && !this.pages?.length) return;
      const band = Math.min(this.headerOffset, Math.max(0, innerHeight - 100));
      this.observer = new IntersectionObserver(() => this.schedule(), { rootMargin: `-${band}px 0px -${Math.max(0, innerHeight - band - 100)}px 0px`, threshold: 0 });
      new Set([...this.items, ...this.pages].map(x => x.target)).forEach(target => this.observer.observe(target));
      const rect = this.getBoundingClientRect();
      this.critical = [...document.querySelectorAll('.product-meta__price-list-container, .product-form__variants, .product-form__buy-buttons, .product__media, product-sticky-form, .product-sticky-form, .header__icon-list')];
      this.collisionObserver = new IntersectionObserver(() => this.schedule(), { rootMargin: `-${Math.max(0, rect.top - 8)}px 0px -${Math.max(0, innerHeight - rect.bottom - 8)}px 0px`, threshold: 0 });
      this.critical.forEach(node => this.collisionObserver.observe(node));
    }
    activate(item) {
      for (const entry of this.items) {
        if (entry === item) entry.link.setAttribute('aria-current', 'location');
        else entry.link.removeAttribute('aria-current');
      }
    }
    update() {
      if (this.pageIndicator) {
        const page = this.currentItem(this.pages);
        this.pageIndicator.hidden = !page;
        if (page) this.pageIndicator.textContent = '第 ' + (this.pages.indexOf(page) + 1) + ' / ' + this.pages.length + ' 页 · ' + page.pageLabel;
      }
      if (!this.items?.length || this.hidden) return;
      const active = this.currentItem(this.items);
      this.activate(active);
      const background = getComputedStyle(active.story || active.section || active.target).backgroundColor || '';
      const rgb = background.match(/\d+(?:\.\d+)?/g)?.map(Number);
      this.classList.toggle('on-dark', !!rgb && rgb.length >= 3 && (rgb.length < 4 || rgb[3] > .5) && (rgb[0] * .299 + rgb[1] * .587 + rgb[2] * .114) < 100);
      const rect = this.getBoundingClientRect();
      const overlaps = node => {
        const box = node.getBoundingClientRect(),style = getComputedStyle(node);
        return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0 && box.width > 0 && box.height > 0 && box.left < rect.right && box.right > rect.left && box.top < rect.bottom && box.bottom > rect.top;
      };
      const overlay = this.overlays.some(x => x.hasAttribute('open') && x.getAttribute('aria-hidden') !== 'true');
      const occluded = overlay || (this.critical || []).some(overlaps);
      const beforeDetails = this.beforeDetailStart();
      const overVideo = this.overlapsPageVideo(rect);
      this.classList.toggle('is-occluded', occluded);
      this.classList.toggle('is-before-details', beforeDetails);
      this.classList.toggle('is-over-video', overVideo);
      this.inert = occluded || beforeDetails || overVideo;
      this.classList.toggle('is-over-text', !this.inert && this.classList.contains('is-expanded') && this.overlapsPageText(this.list.getBoundingClientRect()));
      this.dataset.visibilityReady = 'true';
    }
    schedule(reobserve = false, rebuild = false) {
      this.needsObserve ||= reobserve; this.needsBuild ||= rebuild;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.needsBuild) { this.build(); this.needsBuild = false; }
        this.setOffsets();
        if (this.needsObserve) { this.observe(); this.needsObserve = false; }
        this.update();
      });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      if (this.pageIndicator) this.pageIndicator.hidden = true;
      this.observer?.disconnect(); this.collisionObserver?.disconnect(); this.resizeObserver?.disconnect();
      this.overlayObserver?.disconnect(); this.storyObserver?.disconnect(); this.reviewObservers?.forEach(x => x.disconnect());
      this.textViewportObserver?.disconnect(); this.textContentObserver?.disconnect(); this.videoViewportObserver?.disconnect();
      this.videoRegions = []; this.detailSections = [];
      delete this.dataset.visibilityReady;
      clearTimeout(this.textCollisionTimer); this.textCollisionTimer = null;
      this.visibleText?.clear(); this.textGroups?.clear();
      if (this.frame) cancelAnimationFrame(this.frame); this.frame = null;
      this.restores?.reverse().forEach(restore => restore()); this.restores = [];
      delete this.dataset.initialized;
    }
  }
  customElements.define('fgg-product-section-nav', FggProductSectionNav);
})();
